/**
 * useDelegates
 * The external delegates of a work order or project with the state of their
 * personal links, via the admin `guestTokenApi` list action. Refetch after a
 * delegation changes; `enabled` gates the call (admin only, and only while a
 * task has delegates).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { guestTokensApi } from '../api/guestTokensApi';
import { Delegate, GuestTaskType } from '../types/guestAccess';

interface UseDelegatesReturn {
    delegates: Delegate[];
    /** Delegates keyed by externalUserId */
    byUid: Record<string, Delegate>;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

export function useDelegates(taskType: GuestTaskType, taskKey: string | null, enabled: boolean): UseDelegatesReturn {
    const [delegates, setDelegates] = useState<Delegate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Only the latest request may set state: a host that switches task must
    // never show the previous task's delegates.
    const latestRequest = useRef(0);

    const refetch = useCallback(async () => {
        const request = ++latestRequest.current;
        if (!enabled || !taskKey) {
            setDelegates([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const listed = await guestTokensApi.list(taskType, taskKey);
            if (request === latestRequest.current) setDelegates(listed);
        } catch (err) {
            if (request !== latestRequest.current) return;
            console.error('Error fetching delegates:', err);
            setError('Kunde inte hämta externa mottagare');
        } finally {
            if (request === latestRequest.current) setLoading(false);
        }
    }, [taskType, taskKey, enabled]);

    useEffect(() => {
        void refetch();
    }, [refetch]);

    const byUid: Record<string, Delegate> = {};
    for (const delegate of delegates) byUid[delegate.externalUserId] = delegate;

    return { delegates, byUid, loading, error, refetch };
}
